import React, { Suspense, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import {
    capitalize,
    Checkbox, FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    TextField
} from '@mui/material';
import { isFProject } from 'config/project';
import { CompanyType } from 'models/CompanyModel';
import { VehicleType } from 'models/StockModel';
import useCarsFilter from 'modules/cars/hooks/useCarsFilter';
import CarsDesktop from 'modules/cars/views/CarsDesktop';
import CarsMobile from 'modules/cars/views/CarsMobile';
import auth from 'recoil/atoms/auth';
import loadingYardList from 'recoil/selectors/loadingYardList';


const vehicleTypes = Object.values(VehicleType);

const CarsInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const location = useLocation();
    const yards = useRecoilValue(loadingYardList);
    const {
        filter,
        changeFilter,
        cars,
        yardFilterOptions,
        voyageFilterOptions,
        companyFilterOptions,
    } = useCarsFilter();

    const [arrivedCount, docsCount] = useMemo(() => {
        return [
            cars.filter(c => c.yard?.arrived).length,
            cars.filter(c => c.documents.exportCertificate || c.documents.vehicleInvoice).length,
        ];
    }, [cars]);

    const filterInputs = [];

    if (isMobile) {
        filterInputs.push(
            <TextField
                select
                id="yard"
                key="yard"
                name="yard"
                label={t('yard')}
                fullWidth
                variant="outlined"
                defaultValue={filter.yard === undefined ? '' : filter.yard}
                onChange={e => changeFilter(e.target.name, e.target.value)}
            >
                {yardFilterOptions.map(({value, label}) =>
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                )}
            </TextField>
        );
    }

    filterInputs.push(
        <TextField
            select
            id="voyage"
            key="voyage"
            name="voyage"
            label={t('voyage')}
            fullWidth
            variant="outlined"
            defaultValue={filter.voyage === undefined ? '' : filter.voyage}
            onChange={e => changeFilter(e.target.name, e.target.value)}
        >
            {voyageFilterOptions.map(({value, label}) =>
                <MenuItem key={value} value={value}>{label}</MenuItem>
            )}
        </TextField>
    );

    if (companyFilterOptions.length > 2) {
        filterInputs.push(
            <TextField
                select
                id="shipper"
                key="shipper"
                name="shipper"
                label={t('shipper')}
                fullWidth
                variant="outlined"
                defaultValue={filter.shipper === undefined ? '' : filter.shipper}
                onChange={e => changeFilter(e.target.name, e.target.value)}
            >
                {companyFilterOptions.map(({value, label}) =>
                    <MenuItem key={value} value={value}>{label}</MenuItem>
                )}
            </TextField>
        );
    }

    filterInputs.push(
        <TextField
            select
            id="state"
            key="state"
            name="state"
            label={t('state')}
            fullWidth
            variant="outlined"
            defaultValue={filter.state === undefined ? '' : filter.state}
            onChange={e => changeFilter(e.target.name, e.target.value)}
        >
            <MenuItem value="">{t('any')}</MenuItem>
            <MenuItem value="unknown">{t('unknown')}</MenuItem>
            <MenuItem value="whole">{t('whole')}</MenuItem>
            <MenuItem value="dis">{t('dismantled')}</MenuItem>
            {!isFProject && <MenuItem value="dmg">{t('damaged')}</MenuItem>}
        </TextField>
    );

    filterInputs.push(
        <FormControl key="vehicle_type" fullWidth margin="dense" size="small">
            <InputLabel id="vehicle-type-label">{t('vehicle_type')}</InputLabel>
            <Select
                multiple
                labelId="vehicle-type-label"
                id="vehicle_type"
                name="vehicleType"
                input={<OutlinedInput label={t('vehicle_type')}/>}
                value={filter.vehicleType || []}
                renderValue={selected => selected.join(', ')}
                onChange={e => {
                    const {
                        target: {value},
                    } = e;
                    // On autofill, we get a stringified value
                    const val: string[] = typeof value === 'string' ? value.split(',') : value as string[]
                    changeFilter(e.target.name, val);
                }}
            >
                {vehicleTypes.map((type) => (
                    <MenuItem key={type} value={type} dense>
                        <Checkbox checked={!!filter.vehicleType && filter.vehicleType.indexOf(type) > -1}/>
                        <ListItemText primary={capitalize(type)}/>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    filterInputs.push(
        <TextField
            select
            id="photos"
            key="photos"
            name="photos"
            label={t('photos')}
            fullWidth
            variant="outlined"
            defaultValue={filter.photos === undefined ? '' : filter.photos}
            onChange={e => changeFilter(e.target.name, e.target.value === '' ? '' : Number(e.target.value))}
        >
            <MenuItem value="">{t('any')}</MenuItem>
            <MenuItem value="1">{t('with')}</MenuItem>
            <MenuItem value="0">{t('without')}</MenuItem>
        </TextField>
    );

    filterInputs.push(
        <TextField
            select
            id="docs"
            key="docs"
            name="docs"
            label={t('docs')}
            fullWidth
            variant="outlined"
            defaultValue={filter.docs === undefined ? '' : filter.docs}
            onChange={e => changeFilter(e.target.name, e.target.value === '' ? '' : Number(e.target.value))}
        >
            <MenuItem value="">{t('any')}</MenuItem>
            <MenuItem value="1">{t('with')}</MenuItem>
            <MenuItem value="0">{t('without')}</MenuItem>
        </TextField>
    );

    const View = isMobile ? CarsMobile : CarsDesktop;

    return (
        <View
            cars={cars}
            yards={yards}
            yardFilterOptions={yardFilterOptions}
            query={location.search}
            canViewCompany={my.type !== CompanyType.Shipper}
            filterInputs={filterInputs}
            carCountArrived={arrivedCount}
            carCountDocs={docsCount}
        />
    )
};

const Cars = () => {
    return (
        <Suspense fallback={null}>
            <CarsInner/>
        </Suspense>
    )
}

export default Cars;
